import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { Clipboard } from '@angular/cdk/clipboard'
import { email, FieldTree, form, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute, Data, Router } from '@angular/router'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { map, switchMap, takeUntil } from 'rxjs/operators'

import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { LocalDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { TilintarkastajaComponentData } from 'app/_angular/_resolvers/tilintarkastaja.resolve'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { asetaArvo, nollaa } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { Tilintarkastaja, TilintarkastajaKutsu, TilintarkastajaLoginInformation, TilintarkastajanTila, TilintarkastajaOikeus } from 'app/_jaettu-lemonator/model/tilintarkastaja'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { TilintarkastajaUriService } from 'app/_jaettu-lemonator/service/tilintarkastaja-uri.service'
import {
  TILINTARKASTAJA_KUTSU_VIESTIN_AIHE,
  TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_ALA,
  TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI,
  TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_YLA,
  TILINTARKASTAJAN_TILA_TEKSTI,
  TilintarkastajaService,
} from 'app/_jaettu-lemonator/service/tilintarkastaja.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { TilintarkastajaLoginHistoryDialog } from './tilintarkastaja.login-history.dialog'

interface TilintarkastajaKutsuNimitiedoilla extends TilintarkastajaKutsu {
  luojaNimi: string
  sanitizedViestinSisalto?: SafeHtml
}

interface NaytettavaTilintarkastajaOikeus extends TilintarkastajaOikeus {
  asiakasAvain: string
  asiakasNimi: string
  tilikausiAlkaa: LocalDate
  tilikausiLoppuu: LocalDate
}

interface TilintarkastajaLomake {
  etunimi: string
  sukunimi: string
  email: string
  hetu: string
  tila: TilintarkastajanTila | null
  poistettu: boolean
}

const TYHJA_LOMAKE: TilintarkastajaLomake = { etunimi: '', sukunimi: '', email: '', hetu: '', tila: null, poistettu: false }

interface KutsuviestiLomake {
  kutsuAihe: string
  kutsuViestiYla: string
  kutsuViestiAla: string
}

@Component({
  templateUrl: './tilintarkastaja.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TilintarkastajaComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()

  tilintarkastajaUriObservable: Observable<string>
  tilintarkastajaEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  kirjanpitajaOnSuperObservable: Observable<boolean>
  kutsutEncodedUriObservable: Observable<string>
  oikeudetEncodedUriObservable: Observable<string>

  // Tilintarkastajan tiedot
  lomake: FieldTree<TilintarkastajaLomake>
  readonly tilintarkastaja = signal<Tilintarkastaja | null>(null)
  loading = true

  // Tilintarkastajan kutsut
  kutsutObservable: Observable<TilintarkastajaKutsuNimitiedoilla[]> = null
  kirjanpitajienNimitiedotObservable: Observable<TilintarkastajaKutsuNimitiedoilla[]>
  expandedKutsuIndex: number | null = null

  // Tilintarkastajan oikeudet
  oikeudetObservable: Observable<NaytettavaTilintarkastajaOikeus[]> = null
  naytaVainVoimassaolevatOikeudetSubject = new BehaviorSubject<boolean>(true)

  readonly TILINTARKASTAJAN_TILA_TEKSTI = TILINTARKASTAJAN_TILA_TEKSTI

  // Kutsuviestin muokkaus
  readonly TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI = TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI
  kutsuviestiLomake = form(
    signal<KutsuviestiLomake>({ kutsuAihe: TILINTARKASTAJA_KUTSU_VIESTIN_AIHE, kutsuViestiYla: TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_YLA, kutsuViestiAla: TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_ALA }),
    polku => {
      required(polku.kutsuAihe)
      required(polku.kutsuViestiYla)
      required(polku.kutsuViestiAla)
    },
  )

  private readonly _injector = inject(Injector)

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _firebase: FirebaseLemonator,
    private _timestampService: TimestampService,
    private _errorHandler: ErrorHandler,
    private _tilintarkastajaService: TilintarkastajaService,
    private _tilintarkastajaUriService: TilintarkastajaUriService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _asiakasUriService: AsiakasUriService,
    private _ladataanService: LadataanService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _dateService: DateService,
    private _debugService: DebugService,
    private _sanitizer: DomSanitizer,
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _clipboard: Clipboard,
  ) {}

  ngOnInit() {
    this.lomake = form(
      signal<TilintarkastajaLomake>(TYHJA_LOMAKE),
      polku => {
        required(polku.etunimi)
        required(polku.sukunimi)
        required(polku.email)
        email(polku.email)
      },
      { injector: this._injector },
    )

    // TODO TILINTARKASTAJAT: Huomaa, ettei tämä päivity - olisi hyvä olla observable, jotta päivittyy !
    this._route.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data: Data) => {
      const componentData = data.data as TilintarkastajaComponentData

      nollaa(this.lomake, TYHJA_LOMAKE)
      this.tilintarkastaja.set(this._tilintarkastajaService.kopioiTilintarkastaja(componentData.tilintarkastaja))
      this.alustaForm(this.tilintarkastaja())
    })

    this.kutsutObservable = combineLatest(
      this._firebase
        .firestoreCollection<TilintarkastajaKutsu>(this._tilintarkastajaUriService.annaTilintarkastajaKutsutCollectionUri(this._route.snapshot.params['avain']))
        .orderBy('luotu', 'desc')
        .listen(),
      this._kirjanpitajaService.kirjanpitajienNimitiedotObservable,
    ).pipe(
      takeUntil(this.ngUnsubscribe),
      map(([kutsut, kirjanpitajienNimitiedot]) => {
        return kutsut.map(kutsu => {
          const kirjanpitaja = kirjanpitajienNimitiedot.find(k => k.avain === kutsu.luoja)
          return {
            ...kutsu,
            luojaNimi: kirjanpitaja ? `${kirjanpitaja.etunimi} ${kirjanpitaja.sukunimi}` : 'Tuntematon',
            sanitizedViestinSisalto: this._sanitizer.sanitize(1, kutsu.viestinSisalto),
          }
        })
      }),
    )

    this.oikeudetObservable = this.naytaVainVoimassaolevatOikeudetSubject.pipe(
      switchMap(naytaVainVoimassaolevat => {
        let query = this._firebase.firestoreCollection<TilintarkastajaOikeus>(this._tilintarkastajaUriService.annaTilintarkastajaOikeudetCollectionUri(this._route.snapshot.params['avain']))

        // Jos näytetään vain voimassaolevat, lisää where-ehto Firestore-kyselyyn
        if (naytaVainVoimassaolevat) {
          query = query.where('voimassaoloPaattyy', '>=', this._timestampService.now())
        }

        return query
          .orderBy('voimassaoloPaattyy', 'desc')
          .listen()
          .pipe(
            switchMap(async oikeudet => {
              // Haetaan asiakastiedot
              const uniikitAsiakasAvaimet = new Set(oikeudet.map(oikeus => oikeus.asiakasAvain))
              const asiakasPromiset: Promise<Asiakas>[] = []
              for (const avain of uniikitAsiakasAvaimet.values()) {
                asiakasPromiset.push(this._firebase.firestoreDoc<Asiakas>(this._asiakasUriService.annaAsiakasUri(avain)).get())
              }
              return await Promise.all(asiakasPromiset).then(asiakkaat => {
                return oikeudet
                  .map(oikeus => {
                    const asiakas = asiakkaat.find(a => a.avain === oikeus.asiakasAvain)
                    const tilikausi = asiakas?.tilikaudet.find(t => t.avain === oikeus.tilikausiAvain)
                    return {
                      ...oikeus,
                      asiakasAvain: oikeus.asiakasAvain,
                      asiakasNimi: asiakas ? asiakas.nimi : 'Tuntematon',
                      tilikausiAlkaa: tilikausi ? tilikausi.alkaa : null,
                      tilikausiLoppuu: tilikausi ? tilikausi.loppuu : null,
                    }
                  })
                  .sort(
                    (a, b) =>
                      a.asiakasNimi.localeCompare(b.asiakasNimi) || // asc
                      this._dateService.localDateToNumber(a.tilikausiLoppuu) - this._dateService.localDateToNumber(b.tilikausiLoppuu),
                  )
              })
            }),
          )
      }),
      takeUntil(this.ngUnsubscribe),
    )

    // URIT
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.kirjanpitajaOnSuperObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(map(k => k?.rooli === KirjanpitajanRooli.SUPER))
    this.tilintarkastajaUriObservable = this._route.data.pipe(
      map((route: { data: TilintarkastajaComponentData }) => {
        if (route?.data?.tilintarkastaja?.avain) {
          return this._tilintarkastajaUriService.annaTilintarkastajaUri(route.data.tilintarkastaja.avain)
        }
        return ''
      }),
    )
    this.tilintarkastajaEncodedUriObservable = this.tilintarkastajaUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )
    this.kutsutEncodedUriObservable = this.tilintarkastajaUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri + '/tilintarkastaja-kutsut')
      }),
    )
    this.oikeudetEncodedUriObservable = this.tilintarkastajaUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri + '/tilintarkastaja-oikeudet')
      }),
    )
  }

  private alustaForm(tilintarkastaja: Tilintarkastaja) {
    asetaArvo(this.lomake.etunimi, tilintarkastaja.etunimi ?? '')
    asetaArvo(this.lomake.sukunimi, tilintarkastaja.sukunimi ?? '')
    asetaArvo(this.lomake.email, tilintarkastaja.email ?? '')
    asetaArvo(this.lomake.hetu, tilintarkastaja.hetu ?? '')
    asetaArvo(this.lomake.tila, tilintarkastaja.tila ?? null)
    asetaArvo(this.lomake.poistettu, tilintarkastaja.poistettu ?? false)
  }

  async naytaLoginHistoriaa() {
    const loginHistoria = await this._firebase
      .firestoreCollection<TilintarkastajaLoginInformation>(this._tilintarkastajaUriService.annaTilintarkastajaLoginCollectionUri(this.tilintarkastaja().avain))
      .orderBy('timestamp', 'desc')
      .limit(100)
      .get()
      .then(snapshots => snapshots.map(s => s as TilintarkastajaLoginInformation))
    this._dialog.open(TilintarkastajaLoginHistoryDialog, { data: loginHistoria })
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  peruuta() {
    this._router.navigate(['yllapito', 'tilintarkastajat'])
  }

  async save() {
    if (!this.lomake().valid()) {
      return
    }

    this._ladataanService.aloitaLataaminen()

    const kayttajaUid = (await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()).uid
    const val = this.lomake().value()
    const now = this._timestampService.now()
    const tilintarkastaja = this._tilintarkastajaService.kopioiTilintarkastaja(this.tilintarkastaja())

    tilintarkastaja.etunimi = val.etunimi
    tilintarkastaja.sukunimi = val.sukunimi
    tilintarkastaja.email = val.email
    tilintarkastaja.hetu = val.hetu || null
    tilintarkastaja.tila = val.tila || null
    if (!tilintarkastaja.luoja) {
      tilintarkastaja.luoja = kayttajaUid
      tilintarkastaja.luotu = now
    }
    tilintarkastaja.poistettu = val.poistettu

    try {
      const batch = this._firebase.firestoreBatch()
      this._tilintarkastajaService.lisaaTilintarkastajaBatchiin(tilintarkastaja, kayttajaUid, batch, true)
      await batch.commit()
      this.tilintarkastaja.set(tilintarkastaja)
      this.alustaForm(this.tilintarkastaja())
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async kutsuTilintarkastaja() {
    try {
      if (this.kutsuviestiLomake().invalid()) {
        return
      }
      this._ladataanService.aloitaLataaminen()
      const batch = this._firebase.firestoreBatch()
      const now = this._timestampService.now()
      const userId = (await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()).uid
      const { kutsuAihe, kutsuViestiYla, kutsuViestiAla } = this.kutsuviestiLomake().value()

      const kutsu = this._tilintarkastajaService.annaUusiTilintarkastajaKutsu(this.tilintarkastaja().avain, this._firebase.firestoreCreateId())
      const kutsuViesti = this._tilintarkastajaService.annaTilintarkastajaKutsuViesti(kutsuViestiYla, TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI, kutsuViestiAla)
      const tekstiWithBreaks = kutsuViesti.replace(/(?:\r\n|\r|\n)/g, '<br/>')
      kutsu.viestinAihe = kutsuAihe
      kutsu.viestinSisalto = tekstiWithBreaks
      kutsu.vastaanottaja = this.tilintarkastaja().email
      kutsu.luoja = userId
      kutsu.luotu = now
      kutsu.paivittaja = userId
      kutsu.paivitetty = now
      kutsu.tilintarkastajaAvain = this.tilintarkastaja().avain
      this._tilintarkastajaService.lisaaTilintarkastajaKutsuBatchiin(kutsu, batch)
      this._tilintarkastajaService.lisaaTilintarkastajaKutsuTyojonoBatchiin(this.tilintarkastaja().avain, kutsu.avain, batch)
      await batch.commit()
      this._snackbar.open('Kutsuviestin lähetys lisätty työjonoon', null, { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  toggleKutsuDetails(index: number) {
    this.expandedKutsuIndex = this.expandedKutsuIndex === index ? null : index
  }

  naytaVainVoimassaolevatChange(naytaVainVoimassaolevat: boolean) {
    this.naytaVainVoimassaolevatOikeudetSubject.next(naytaVainVoimassaolevat)
  }

  kopioiAvain() {
    if (!this.tilintarkastaja()?.avain) {
      return
    }
    if (this._clipboard.copy(this.tilintarkastaja().avain)) {
      this._snackbar.open('Avain kopioitiin', null, { duration: 1500 })
    }
  }
}
